import { ApiError } from './api';
import type { ChallengeShare, ChallengeSnapshot } from './challenge-core';
import { SESSION_PROTOCOL_VERSION } from './session-core';
import { t } from './i18n';

export const RELATIONSHIP_TYPES = [
  'wife', 'husband', 'partner', 'best_friend', 'friend', 'brother', 'sister',
  'parent', 'child', 'family', 'coworker', 'other'
] as const;
export type RelationshipType = typeof RELATIONSHIP_TYPES[number];
export type PairInteraction = 'same_brain' | 'read_me' | 'dont_match';

export interface ConnectionSummary {
  user: { id: string; firstName: string; photoUrl: string | null; hasCustomPhoto: boolean; customPhotoUpdatedAt: string | null };
  relationshipType: RelationshipType | null;
  firstPlayedAt: string;
  lastPlayedAt: string;
  sessionsTogether: number;
  conceptsExplored: number;
  completedGames: number;
  averageScore10: number | null;
  modeScores: { sameBrain: number | null; readMe: number | null; dontMatch: number | null };
}


export interface ConnectionHistoryItem { sessionId:string; interactionType:PairInteraction; playedAt:string; score:number; total:number; score10:number; }
export interface ConnectionShare { fallbackShareUrl:string; preparedMessageId:string|null; }

export interface CustomQuestionSummary {
  id: string;
  creator: { userId: string; firstName: string };
  question: string;
  optionA: string;
  optionB: string;
  saved: boolean;
  createdAt: string;
  savedAt: string | null;
}

export type InsightEvidence = 'not_enough' | 'emerging' | 'established';
export type PairPattern = 'same' | 'different' | 'mixed' | null;
export interface ConnectionDimensionInsight { dimension: string; label: string; observations: number; same: number; different: number; evidence: InsightEvidence; pattern: PairPattern; copy: string; }
export interface ConnectionInsights { generatedAt: string; evidence: InsightEvidence; observedPairRounds: number; sharedPatterns: ConnectionDimensionInsight[]; differentPatterns: ConnectionDimensionInsight[]; mixedPatterns: ConnectionDimensionInsight[]; unexploredAreas: Array<{ dimension: string; label: string; conceptsExplored: number }>; principle: string; }

export interface ConnectionPlayResult {
  token: string;
  snapshot: ChallengeSnapshot;
  delivery: { sent: boolean; deepLink: string };
  share: ChallengeShare | null;
}

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options,
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `tma ${initData}`,
      'Content-Type': 'application/json',
      'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION),
      ...options?.headers
    }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update this connection.', body.error?.code);
  return body;
};

export const loadConnections = async (initData: string): Promise<ConnectionSummary[]> => {
  const result = await request<{ connections: ConnectionSummary[] }>('/connections', initData);
  return result.connections;
};

export const loadConnection = async (initData: string, connectedUserId: string): Promise<ConnectionSummary> => {
  const result = await request<{ connection: ConnectionSummary }>(`/connections/${encodeURIComponent(connectedUserId)}`, initData);
  return result.connection;
};

export const loadConnectionInsights = async (initData: string, connectedUserId: string): Promise<ConnectionInsights> => {
  const result = await request<{ insights: ConnectionInsights }>(`/connections/${encodeURIComponent(connectedUserId)}/insights`, initData);
  return result.insights;
};

export const updateConnectionLabel = async (
  initData: string,
  connectedUserId: string,
  relationshipType: RelationshipType | null
): Promise<ConnectionSummary> => {
  const result = await request<{ connection: ConnectionSummary }>(`/connections/${encodeURIComponent(connectedUserId)}/label`, initData, {
    method: 'PUT',
    body: JSON.stringify({ relationshipType })
  });
  return result.connection;
};

export const createConnectionPlay = (
  initData: string,
  connectedUserId: string,
  interactionType: PairInteraction
): Promise<ConnectionPlayResult> => request(`/connections/${encodeURIComponent(connectedUserId)}/play`, initData, {
  method: 'POST',
  body: JSON.stringify({ actionId: crypto.randomUUID(), interactionType })
});

export const relationshipLabel = (type: RelationshipType | null): string | null => {
  if (!type) return null;
  return t(({
    wife: 'Wife', husband: 'Husband', partner: 'Partner', best_friend: 'Best Friend', friend: 'Friend', brother: 'Brother', sister: 'Sister',
    parent: 'Parent', child: 'Child', family: 'Family', coworker: 'Coworker', other: 'Other'
  } satisfies Record<RelationshipType, string>)[type]);
};


export const loadCustomQuestions = async (initData: string, connectedUserId: string): Promise<CustomQuestionSummary[]> => {
  const result = await request<{ questions: CustomQuestionSummary[] }>(`/connections/${encodeURIComponent(connectedUserId)}/custom-questions`, initData);
  return result.questions;
};

export const createCustomQuestion = async (
  initData: string,
  connectedUserId: string,
  input: { question: string; optionA: string; optionB: string }
): Promise<CustomQuestionSummary> => {
  const result = await request<{ question: CustomQuestionSummary }>(`/connections/${encodeURIComponent(connectedUserId)}/custom-questions`, initData, {
    method: 'POST',
    body: JSON.stringify({ actionId: crypto.randomUUID(), ...input })
  });
  return result.question;
};

export const playCustomQuestion = (
  initData: string,
  connectedUserId: string,
  promptId: string
): Promise<ConnectionPlayResult> => request(`/connections/${encodeURIComponent(connectedUserId)}/custom-questions/${encodeURIComponent(promptId)}/play`, initData, {
  method: 'POST',
  body: JSON.stringify({ actionId: crypto.randomUUID() })
});

export const saveCustomQuestion = async (
  initData: string,
  connectedUserId: string,
  promptId: string
): Promise<CustomQuestionSummary> => {
  const result = await request<{ question: CustomQuestionSummary }>(`/connections/${encodeURIComponent(connectedUserId)}/custom-questions/${encodeURIComponent(promptId)}/save`, initData, {
    method: 'POST',
    body: '{}'
  });
  return result.question;
};


export const loadConnectionHistory = async (initData:string,connectedUserId:string):Promise<ConnectionHistoryItem[]> =>
  (await request<{history:ConnectionHistoryItem[]}>(`/connections/${encodeURIComponent(connectedUserId)}/history`,initData)).history;

export const requestConnectionShare = async (initData:string,connectedUserId:string):Promise<ConnectionShare> =>
  (await request<{share:ConnectionShare}>(`/connections/${encodeURIComponent(connectedUserId)}/share`,initData,{method:'POST',body:'{}'})).share;

export const openConnectionShare = (share:ConnectionShare):void => {
  const webApp=window.Telegram?.WebApp;
  if(share.preparedMessageId&&webApp?.shareMessage) webApp.shareMessage(share.preparedMessageId);
  else if(webApp?.openTelegramLink) webApp.openTelegramLink(share.fallbackShareUrl);
  else window.open(share.fallbackShareUrl,'_blank','noopener,noreferrer');
};
